import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { KitchenBlueprintInput } from '../types';
import { normalizeSurroundKey, normalizeCountertopKey, getTrimKeyForSurround, VALID_SURROUND_KEYS, VALID_COUNTERTOP_KEYS } from '../helpers';
import { SKY_TEXTURES, FLOOR_TEXTURES } from '../../../../constants/texture-options';
import { getEffectiveTextureQuality, getSkyResolutionForQuality, type SkyResolution } from '../../../../hooks/use-three-kitchen/quality';
import type { SkyResolutionVariants } from '../../../../constants/texture-options';

/**
 * Model requirements for the scene
 */
export interface ModelRequirement {
	id: string;
	sku: string;
	islandIndex: number;
}

/**
 * Requirements for textures and models needed to render a kitchen scene.
 * Used to prioritize loading and enable gated progressive rendering.
 */
export interface SceneTextureRequirements {
	/** Phase 1 - Critical textures (selected floor + sky) - blocks scene render */
	critical: {
		floor: string;
		sky: string;
	};

	/** Phase 2 - Scene-specific materials (countertop, surround, trim) */
	sceneMaterials: {
		countertop: string;
		/** All distinct countertop keys used across islands (per-island overrides included) */
		countertops: string[];
		surround: string;
		trim: string;
	};

	/** Phase 3 - Models required for appliances */
	models: ModelRequirement[];

	/** Phase 5 - Background preload: other countertop/surround textures */
	preloadMaterials: string[];

	/** Phase 6 - Background preload: other sky/floor default textures */
	preloadEnvironment: string[];

	/** Phase 7 - Sky quality upgrades (higher resolution variants) */
	qualityUpgrades: Array<{ key: string; targetTexture: string }>;
}

/**
 * Select the target sky texture for quality upgrade
 */
const selectSkyTextureForQuality = (
	textures: SkyResolutionVariants | undefined,
	defaultTexture: string,
	renderQuality: number
): { defaultTexture: string; targetTexture: string; needsUpgrade: boolean } => {
	const resolutionChain = getSkyResolutionForQuality(renderQuality);

	if (resolutionChain.length === 0 || !textures) {
		return { defaultTexture, targetTexture: defaultTexture, needsUpgrade: false };
	}

	let targetTexture = defaultTexture;
	for (const resolution of resolutionChain) {
		if (resolution === 'default') {
			targetTexture = defaultTexture;
			break;
		}
		const textureFile = textures[resolution as keyof SkyResolutionVariants];
		if (textureFile) {
			targetTexture = textureFile;
			break;
		}
	}

	return {
		defaultTexture,
		targetTexture,
		needsUpgrade: targetTexture !== defaultTexture
	};
};

/**
 * Analyze kitchen input to determine which textures and models are required.
 *
 * This enables prioritized loading:
 * - Critical textures (floor + sky) load first and block scene render
 * - Scene materials (countertop, surround) load in parallel with scene composition
 * - Models are queued for download
 * - Objects wait (gate) on their specific texture/model before transitioning in
 * - Remaining textures preload in the background for instant switching
 *
 * @param self - KitchenSceneBuilder instance
 * @param input - Kitchen blueprint input data
 * @returns Requirements organized by loading phase
 */
export const analyzeTextureRequirementsCfn = (
	self: KitchenSceneBuilder,
	{ input }: { input: KitchenBlueprintInput }
): SceneTextureRequirements => {
	const { renderQuality, textureQuality } = self.configAccess;
	const effectiveTextureQuality = getEffectiveTextureQuality(textureQuality, renderQuality);

	// --- Phase 1: Critical textures ---
	const floorKey = input.floorKey || 'floor/grid-tile';
	const skyKey = input.skyKey || 'sky/overcast';

	// --- Phase 2: Scene materials ---
	const kitchenMaterial = self.kitchenDataManagerAccess.getKitchenMaterial();
	const surroundKey = normalizeSurroundKey(kitchenMaterial?.surround?.key);
	const countertopKey = normalizeCountertopKey(kitchenMaterial?.countertop?.key);
	const trimKey = getTrimKeyForSurround(surroundKey);

	// --- Phase 3: Models ---
	const models: ModelRequirement[] = [];
	const islands = self.kitchenDataManagerAccess.getIslands();

	// Per-island countertop materials can override the kitchen default (e.g. "No Countertop"),
	// so collect every key actually used and load them all as scene materials.
	const usedCountertopKeys = new Set<string>([countertopKey]);

	for (let islandIndex = 0; islandIndex < islands.length; islandIndex++) {
		const island = islands[islandIndex];

		if (island.counter) {
			usedCountertopKeys.add(normalizeCountertopKey(island.counter?.material?.key));
		}

		if (island.panels) {
			for (const panel of island.panels) {
				if (panel.applianceGroups) {
					for (const applianceGroup of panel.applianceGroups) {
						if (applianceGroup.items) {
							for (const item of applianceGroup.items) {
								if (item.sku) {
									models.push({
										id: item.id || `model-${islandIndex}-${models.length}`,
										sku: item.sku,
										islandIndex
									});
								}
							}
						}
					}
				}
			}
		}
	}

	// --- Phase 5: Preload materials ---
	// Get all valid surround/countertop keys except the ones already used
	const preloadMaterials: string[] = [];

	for (const key of VALID_SURROUND_KEYS) {
		if (key !== surroundKey) {
			preloadMaterials.push(key);
		}
	}

	for (const key of VALID_COUNTERTOP_KEYS) {
		if (key !== countertopKey) {
			preloadMaterials.push(key);
		}
	}

	// --- Phase 6: Preload environment textures ---
	const preloadEnvironment: string[] = [];

	// Other floor textures
	for (const floorOption of FLOOR_TEXTURES) {
		if (floorOption.key !== floorKey && floorOption.texture) {
			preloadEnvironment.push(floorOption.key);
		}
	}

	// Other sky textures (default resolution only for preload)
	for (const skyOption of SKY_TEXTURES) {
		if (skyOption.key !== skyKey && skyOption.key !== 'background' && skyOption.texture) {
			preloadEnvironment.push(skyOption.key);
		}
	}

	// --- Phase 7: Sky quality upgrades ---
	const qualityUpgrades: Array<{ key: string; targetTexture: string }> = [];

	// Check if current sky needs quality upgrade
	const currentSkyOption = SKY_TEXTURES.find((s) => s.key === skyKey);
	if (currentSkyOption?.textures) {
		const selection = selectSkyTextureForQuality(
			currentSkyOption.textures,
			currentSkyOption.texture,
			effectiveTextureQuality
		);
		if (selection.needsUpgrade) {
			qualityUpgrades.push({
				key: skyKey,
				targetTexture: selection.targetTexture
			});
		}
	}

	return {
		critical: {
			floor: floorKey,
			sky: skyKey
		},
		sceneMaterials: {
			countertop: countertopKey,
			countertops: [...usedCountertopKeys],
			surround: surroundKey,
			trim: trimKey
		},
		models,
		preloadMaterials,
		preloadEnvironment,
		qualityUpgrades
	};
};
